/**
 * Registration Flow Hooks - Phase 3 Implementation
 *
 * Custom hooks for managing registration workflow state and logic.
 * Extracts complex state management from components.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useRegistrationContext } from '../contexts/RegistrationContext';
import { LayerType, LayerMetadata } from '../../../types/layerMetadata';
import { LayerStrategyFactory } from '../layers/LayerStrategy';

// ============================================================================
// REGISTRATION FLOW HOOK
// ============================================================================

export interface RegistrationFlowState {
  currentStep: number;
  totalSteps: number;
  canAdvance: boolean;
  canGoBack: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  stepProgress: number; // 0-100
  stepName: string;
  stepDescription: string;
}

export const useRegistrationFlow = () => {
  const {
    state,
    setCurrentStep,
    advanceStep,
    goBackStep,
    validateCurrentStep,
  } = useRegistrationContext();

  // Step names/descriptions depend on layer: Composite (C) has Component Selection at step 2
  const stepNames = useMemo(() => {
    if (state.selectedLayer === 'C') {
      return [
        'Layer Selection',
        'Taxonomy Selection',
        'Component Selection',
        'File Upload & Asset Type',
        'Review & Submit',
      ];
    }
    if (['T', 'R', 'P'].includes(state.selectedLayer || '')) {
      return [
        'Layer Selection',
        'Taxonomy Selection',
        'File Upload',
        'Metadata Review',
        'Component Selection',
        'Final Review',
      ];
    }
    return [
      'Layer Selection',
      'Taxonomy Selection',
      'File Upload',
      'Metadata Review',
      'Final Review',
    ];
  }, [state.selectedLayer]);

  const stepDescriptions = useMemo(() => {
    if (state.selectedLayer === 'C') {
      return [
        'Choose the type of asset to create',
        'Select category and subcategory',
        'Select one Song, Star, Look, Move, and World',
        'Upload your file and set asset type',
        'Review and submit',
      ];
    }
    if (['T', 'R', 'P'].includes(state.selectedLayer || '')) {
      return [
        'Choose the type of asset to create',
        'Select category and subcategory',
        'Upload your files and generate AI metadata',
        'Review and edit AI-generated details',
        'Select component assets',
        'Final review and submit',
      ];
    }
    return [
      'Choose the type of asset to create',
      'Select category and subcategory',
      'Upload your files and generate AI metadata',
      'Review and edit AI-generated details',
      'Final review and submit',
    ];
  }, [state.selectedLayer]);

  // Calculate effective total steps based on layer
  const totalSteps = useMemo(() => {
    // Composite (C): 5 steps (Component Selection before File Upload)
    if (state.selectedLayer === 'C') return 5;
    // T, R, P: 6 steps with component selection
    if (['T', 'R', 'P'].includes(state.selectedLayer || '')) return 6;
    // G, S, L, M, W, B: 5 steps
    return 5;
  }, [state.selectedLayer]);

  // Get current step info
  const flowState: RegistrationFlowState = useMemo(() => {
    const isFirstStep = state.currentStep === 0;
    const isLastStep = state.currentStep === totalSteps - 1;
    const canGoBack = !isFirstStep && !state.isSubmitting;
    const canAdvance = validateCurrentStep() && !state.isSubmitting;
    const stepProgress = Math.round(
      (state.currentStep / (totalSteps - 1)) * 100
    );

    console.log(`🔄 [FLOW STATE] Recomputing flow state:`, {
      currentStep: state.currentStep,
      selectedLayer: state.selectedLayer,
      canAdvance,
      isSubmitting: state.isSubmitting
    });

    return {
      currentStep: state.currentStep,
      totalSteps,
      canAdvance,
      canGoBack,
      isFirstStep,
      isLastStep,
      stepProgress,
      stepName: stepNames[state.currentStep] || 'Unknown Step',
      stepDescription:
        stepDescriptions[state.currentStep] || 'Step description',
    };
  }, [
    state.currentStep,
    state.selectedLayer,
    state.taxonomySelection,
    state.uploadedFiles,
    state.aiResults,
    state.layerMetadata,
    state.formData,
    state.isSubmitting,
    totalSteps,
    validateCurrentStep
  ]);

  // Step navigation functions
  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [setCurrentStep, totalSteps]
  );

  const nextStep = useCallback(() => {
    if (flowState.canAdvance && !flowState.isLastStep) {
      advanceStep();
    }
  }, [advanceStep, flowState.canAdvance, flowState.isLastStep]);

  const previousStep = useCallback(() => {
    if (flowState.canGoBack && !flowState.isFirstStep) {
      goBackStep();
    }
  }, [goBackStep, flowState.canGoBack, flowState.isFirstStep]);

  return {
    flowState,
    goToStep,
    nextStep,
    previousStep,
    validateCurrentStep,
  };
};

// ============================================================================
// LAYER VALIDATION HOOK
// ============================================================================

export interface ValidationState {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  requiredFields: string[];
  optionalFields: string[];
  missingRequired: string[];
}

export const useLayerValidation = (layerCode?: LayerType) => {
  const { state } = useRegistrationContext();
  const activeLayer = layerCode || state.selectedLayer;

  const [validationState, setValidationState] = useState<ValidationState>({
    isValid: false,
    errors: [],
    warnings: [],
    requiredFields: [],
    optionalFields: [],
    missingRequired: [],
  });

  const validateMetadata = useCallback(
    (metadata: LayerMetadata) => {
      if (!activeLayer) {
        setValidationState({
          isValid: false,
          errors: ['No layer selected'],
          warnings: [],
          requiredFields: [],
          optionalFields: [],
          missingRequired: [],
        });
        return false;
      }

      try {
        const strategy = LayerStrategyFactory.getStrategy(activeLayer);
        const validation = strategy.validateMetadata(metadata);
        const requiredFields = strategy.getRequiredFields();
        const optionalFields = strategy.getOptionalFields();

        const missingRequired = requiredFields.filter(field => {
          const value = metadata[field];
          return (
            !value ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')
          );
        });

        const newValidationState: ValidationState = {
          isValid: validation.isValid,
          errors: validation.errors.map(e => e.message),
          warnings: validation.warnings.map(w => w.message),
          requiredFields,
          optionalFields,
          missingRequired,
        };

        setValidationState(newValidationState);
        return validation.isValid;
      } catch (error) {
        console.error('Validation error:', error);
        setValidationState({
          isValid: false,
          errors: ['Validation failed'],
          warnings: [],
          requiredFields: [],
          optionalFields: [],
          missingRequired: [],
        });
        return false;
      }
    },
    [activeLayer]
  );

  // Auto-validate when metadata changes
  useEffect(() => {
    if (state.layerMetadata) {
      validateMetadata(state.layerMetadata);
    }
  }, [state.layerMetadata, validateMetadata]);

  return {
    validationState,
    validateMetadata,
    isValid: validationState.isValid,
    hasErrors: validationState.errors.length > 0,
    hasWarnings: validationState.warnings.length > 0,
  };
};

// ============================================================================
// METADATA GENERATION HOOK
// ============================================================================

export interface MetadataGenerationState {
  isGenerating: boolean;
  progress: number;
  stage: string;
  error: string | null;
  results: any | null;
  canRegenerate: boolean;
}

export const useMetadataGeneration = () => {
  const { state, dispatch } = useRegistrationContext();
  const [generationState, setGenerationState] =
    useState<MetadataGenerationState>({
      isGenerating: false,
      progress: 0,
      stage: '',
      error: null,
      results: null,
      canRegenerate: false,
    });

  const generateMetadata = useCallback(
    async (files: File[], description?: string) => {
      if (!state.selectedLayer || !state.taxonomySelection) {
        throw new Error(
          'Layer and taxonomy must be selected before generating metadata'
        );
      }

      setGenerationState({
        isGenerating: true,
        progress: 0,
        stage: 'Preparing request...',
        error: null,
        results: null,
        canRegenerate: false,
      });

      dispatch({ type: 'START_AI_PROCESSING' });

      try {
        // Simulate AI processing stages
        setGenerationState(prev => ({
          ...prev,
          progress: 25,
          stage: 'Uploading files...',
        }));

        // TODO: Implement actual AI service call
        await new Promise(resolve => setTimeout(resolve, 1000));

        setGenerationState(prev => ({
          ...prev,
          progress: 50,
          stage: 'Analyzing content...',
        }));
        await new Promise(resolve => setTimeout(resolve, 1000));

        setGenerationState(prev => ({
          ...prev,
          progress: 75,
          stage: 'Generating metadata...',
        }));
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Generate layer-specific mock metadata
        const generateLayerMetadata = (layerCode: string) => {
          switch (layerCode) {
            case 'G': // Songs
              return {
                layerType: 'songs',
                songTitle: 'AI Generated Song Title',
                artist: 'AI Generated Artist',
                album: 'AI Generated Album',
                duration: 180,
                tempo: 120,
                key: 'C Major',
                genre: ['Electronic', 'Ambient'],
                mood: ['Relaxing', 'Uplifting'],
                energy: 'medium' as const,
                danceability: 0.7,
                acousticness: 0.3,
                instrumentalness: 0.8,
                valence: 0.6,
              };
            case 'S': // Stars
              return {
                layerType: 'stars',
                name: 'AI Generated Star Name',
                age: Math.floor(Math.random() * 50) + 20,
                gender: 'Non-binary',
                personality: ['Creative', 'Energetic', 'Friendly'],
                appearance: {
                  height: '5\'6"',
                  build: 'Average',
                  hairColor: 'Brown',
                  eyeColor: 'Blue',
                },
                skills: ['Singing', 'Dancing', 'Acting'],
                backstory: 'AI-generated backstory for this star character.',
              };
            case 'L': // Looks
              return {
                layerType: 'looks',
                name: 'AI Generated Look',
                style: 'Modern Casual',
                colors: ['Blue', 'White', 'Gray'],
                season: 'Spring',
                occasion: 'Everyday',
                components: ['Top', 'Bottom', 'Shoes'],
                materials: ['Cotton', 'Denim'],
                fits: ['Regular', 'Comfort'],
              };
            case 'M': // Moves
              return {
                layerType: 'moves',
                name: 'AI Generated Move',
                danceStyle: 'Contemporary',
                difficulty: 'Intermediate',
                duration: 30,
                bodyParts: ['Arms', 'Legs', 'Torso'],
                rhythm: 'Moderate',
                energy: 'High',
                coordination: 'Medium',
              };
            case 'W': // Worlds
              return {
                layerType: 'worlds',
                name: 'AI Generated World',
                environment: 'Urban',
                timeOfDay: 'Evening',
                weather: 'Clear',
                mood: 'Vibrant',
                architecture: 'Modern',
                lighting: 'Ambient',
                population: 'Moderate',
                sounds: ['City Traffic', 'Music'],
              };
            default:
              return {
                layerType: layerCode.toLowerCase(),
                name: 'AI Generated Asset',
                description: description || 'AI-generated description',
              };
          }
        };

        // Mock successful response
        const mockResults = {
          description: description || 'AI-generated description based on uploaded content',
          tags: ['AI-generated', 'Creative', 'Digital'],
          layerMetadata: generateLayerMetadata(state.selectedLayer),
          confidence: 0.85,
          processingMethod: 'hybrid-ai-architecture',
        };

        setGenerationState({
          isGenerating: false,
          progress: 100,
          stage: 'Complete',
          error: null,
          results: mockResults,
          canRegenerate: true,
        });

        dispatch({ type: 'SET_AI_RESULTS', payload: mockResults });

        // Also set the layer metadata for the metadata review step
        dispatch({ type: 'SET_LAYER_METADATA', payload: mockResults.layerMetadata });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'AI generation failed';

        setGenerationState({
          isGenerating: false,
          progress: 0,
          stage: '',
          error: errorMessage,
          results: null,
          canRegenerate: true,
        });

        dispatch({ type: 'SET_AI_ERROR', payload: errorMessage });
      }
    },
    [state.selectedLayer, state.taxonomySelection, dispatch]
  );

  const regenerateMetadata = useCallback(() => {
    if (state.uploadedFiles.length > 0) {
      const files = state.uploadedFiles.map(f => new File([], f.fileName));
      return generateMetadata(files);
    }
  }, [state.uploadedFiles, generateMetadata]);

  const clearResults = useCallback(() => {
    setGenerationState({
      isGenerating: false,
      progress: 0,
      stage: '',
      error: null,
      results: null,
      canRegenerate: false,
    });
    dispatch({ type: 'CLEAR_AI_RESULTS' });
  }, [dispatch]);

  return {
    generationState,
    generateMetadata,
    regenerateMetadata,
    clearResults,
    isGenerating: generationState.isGenerating,
    hasResults: !!generationState.results,
    hasError: !!generationState.error,
  };
};

// ============================================================================
// FORM MANAGEMENT HOOK
// ============================================================================

export const useRegistrationForm = () => {
  const { state, dispatch, updateMetadataField } = useRegistrationContext();

  const updateField = useCallback(
    (field: string, value: any) => {
      updateMetadataField(field, value);
    },
    [updateMetadataField]
  );

  const updateFormData = useCallback(
    (data: Record<string, any>) => {
      dispatch({ type: 'UPDATE_FORM_DATA', payload: data });
    },
    [dispatch]
  );

  const resetForm = useCallback(() => {
    dispatch({ type: 'SET_LAYER_METADATA', payload: null });
    dispatch({ type: 'UPDATE_FORM_DATA', payload: {} });
    dispatch({ type: 'SET_FORM_DIRTY', payload: false });
  }, [dispatch]);

  const markClean = useCallback(() => {
    dispatch({ type: 'SET_FORM_DIRTY', payload: false });
    dispatch({ type: 'SET_UNSAVED_CHANGES', payload: false });
  }, [dispatch]);

  return {
    formData: state.formData,
    layerMetadata: state.layerMetadata,
    isDirty: state.isDirty,
    hasUnsavedChanges: state.hasUnsavedChanges,
    updateField,
    updateFormData,
    resetForm,
    markClean,
  };
};

// ============================================================================
// SUBMISSION HOOK
// ============================================================================

export const useRegistrationSubmission = () => {
  const { state, dispatch } = useRegistrationContext();

  const submitRegistration = useCallback(async () => {
    if (
      !state.layerMetadata ||
      !state.selectedLayer ||
      !state.taxonomySelection
    ) {
      throw new Error('Required data missing for submission');
    }

    dispatch({ type: 'START_SUBMISSION' });

    try {
      // TODO: Implement actual submission logic
      await new Promise(resolve => setTimeout(resolve, 2000));

      const mockCreatedAsset = {
        id: 'asset-' + Date.now(),
        name: 'Created Asset',
        layer: state.selectedLayer,
        metadata: state.layerMetadata,
      };

      dispatch({ type: 'SET_SUBMISSION_SUCCESS', payload: mockCreatedAsset });

      return mockCreatedAsset;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Submission failed';
      dispatch({ type: 'SET_SUBMISSION_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state, dispatch]);

  const clearSubmissionState = useCallback(() => {
    dispatch({ type: 'SET_SUBMISSION_ERROR', payload: null });
  }, [dispatch]);

  return {
    isSubmitting: state.isSubmitting,
    submissionSuccess: state.submissionSuccess,
    submissionError: state.submissionError,
    createdAsset: state.createdAsset,
    submitRegistration,
    clearSubmissionState,
  };
};
